import React, { useState } from 'react';
import { X, Trash2, Plus, ChevronDown, ChevronRight, DoorOpen, Square, Ungroup, Scissors } from 'lucide-react';
import { formatLength, formatArea, distance } from '../utils/geometry';

// Segment-Optionen für Bögen
const ARC_SEGMENT_OPTIONS = [4, 6, 8, 12, 16, 24, 32];

export function PropertiesPanel({
  selectedPoint,
  selectedWall,
  selectedSpace,
  startPoint,
  endPoint,
  spacePolygon,
  componentTypes = [],
  onUpdatePoint,
  onUpdateWall,
  onUpdateSpace,
  onDeletePoint,
  onDeleteWall,
  onDeleteSpace,
  onAddOpening,
  onUpdateOpening,
  onDeleteOpening,
  onDivideWall,
  onClose,
  embedded = false,
  // Arc-Props
  arcs = [],
  getArcForPoint,
  onUpdateArcSegments,
  onDeleteArc,
  onExplodeArc,
}) {
  // Arc für den ausgewählten Punkt finden
  const selectedPointArc = selectedPoint && getArcForPoint
    ? getArcForPoint(selectedPoint.id)
    : null;
  // Nichts ausgewählt
  if (!selectedPoint && !selectedWall && !selectedSpace) {
    return null;
  }

  const content = (
    <>
      {/* Punkt-Eigenschaften */}
      {selectedPoint && (
        <PointProperties
          point={selectedPoint}
          onUpdate={onUpdatePoint}
          onDelete={onDeletePoint}
          arc={selectedPointArc}
          onUpdateArcSegments={onUpdateArcSegments}
          onDeleteArc={onDeleteArc}
          onExplodeArc={onExplodeArc}
        />
      )}

      {/* Wand-Eigenschaften */}
      {selectedWall && startPoint && endPoint && (
        <WallProperties
          wall={selectedWall}
          startPoint={startPoint}
          endPoint={endPoint}
          componentTypes={componentTypes}
          onUpdate={onUpdateWall}
          onDelete={onDeleteWall}
          onDivideWall={onDivideWall}
          onAddOpening={onAddOpening}
          onUpdateOpening={onUpdateOpening}
          onDeleteOpening={onDeleteOpening}
        />
      )}

      {/* Raum-Eigenschaften */}
      {selectedSpace && (
        <SpaceProperties
          space={selectedSpace}
          polygon={spacePolygon}
          componentTypes={componentTypes}
          onUpdate={onUpdateSpace}
          onDelete={onDeleteSpace}
        />
      )}
    </>
  );

  // Im embedded-Modus nur den Inhalt zurückgeben
  if (embedded) {
    return content;
  }

  // Standalone mit eigenem Container
  return (
    <div
      className="absolute right-4 top-4 w-72 bg-white rounded-lg shadow-lg z-10 max-h-[calc(100vh-8rem)] overflow-y-auto"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b">
        <h3 className="font-semibold text-gray-800">
          {selectedPoint && 'Punkt'}
          {selectedWall && 'Wand'}
          {selectedSpace && 'Raum'}
        </h3>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <X size={18} />
        </button>
      </div>
      {content}
    </div>
  );
}

// Punkt-Eigenschaften
function PointProperties({ point, onUpdate, onDelete, arc, onUpdateArcSegments, onDeleteArc, onExplodeArc }) {
  // Punkt-Typ ermitteln
  const pointType = point.isArcThroughPoint
    ? 'Bogen-Scheitelpunkt'
    : point.isArcEndpoint
    ? 'Bogen-Endpunkt'
    : point.isArcIntermediate
    ? 'Bogen-Segment'
    : point.type === 'auxiliary'
    ? 'Hilfspunkt'
    : 'Wandpunkt';

  return (
    <div className="p-3 space-y-3">
      {/* Punkt-Typ Anzeige */}
      <div className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
        Typ: <span className="font-medium text-gray-700">{pointType}</span>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-gray-500">X (mm)</label>
          <input
            type="number"
            value={Math.round(point.x)}
            onChange={(e) => onUpdate?.(point.id, { x: parseFloat(e.target.value) || 0 })}
            className="w-full px-2 py-1 border rounded text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">Y (mm)</label>
          <input
            type="number"
            value={Math.round(point.y)}
            onChange={(e) => onUpdate?.(point.id, { y: parseFloat(e.target.value) || 0 })}
            className="w-full px-2 py-1 border rounded text-sm"
          />
        </div>
      </div>

      <div>
        <label className="text-xs text-gray-500">Höhe (mm)</label>
        <input
          type="number"
          value={Math.round(point.z || 2750)}
          onChange={(e) => onUpdate?.(point.id, { z: parseFloat(e.target.value) || 2750 })}
          className="w-full px-2 py-1 border rounded text-sm"
        />
      </div>

      {/* Arc-Eigenschaften wenn Punkt zu einem Bogen gehört */}
      {arc && (
        <div className="border-t pt-3 mt-3">
          <div className="text-xs font-medium text-cyan-700 mb-2">Bogen-Eigenschaften</div>

          {/* Segmente */}
          <div className="mb-2">
            <label className="text-xs text-gray-500">Segmente</label>
            <select
              value={arc.segments || 8}
              onChange={(e) => onUpdateArcSegments?.(arc.id, Number(e.target.value))}
              className="w-full px-2 py-1 border rounded text-sm"
            >
              {ARC_SEGMENT_OPTIONS.map(n => (
                <option key={n} value={n}>{n} Segmente</option>
              ))}
            </select>
            <div className="text-xs text-gray-400 mt-1">
              Mehr Segmente = glatterer Bogen
            </div>
          </div>

          {/* Info */}
          <div className="text-xs text-gray-500 space-y-1 mb-2">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-cyan-500 inline-block" />
              <span>Endpunkte (türkis) = Bogenbreite</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-orange-500 inline-block" />
              <span>Scheitelpunkt (orange) = Krümmung</span>
            </div>
          </div>

          {/* Bogen explodieren und löschen */}
          <div className="space-y-2">
            <button
              onClick={() => onExplodeArc?.(arc.id)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-amber-50 text-amber-700 rounded hover:bg-amber-100 text-sm"
              title="Bogen in unabhängige Wände auflösen (X)"
            >
              <Ungroup size={14} />
              Bogen explodieren
            </button>
            <div className="text-xs text-gray-400 text-center">
              Wände bleiben erhalten, Bogen-Beziehung wird aufgelöst
            </div>
            <button
              onClick={() => onDeleteArc?.(arc.id)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded hover:bg-red-100 text-sm"
            >
              <Trash2 size={14} />
              Gesamten Bogen löschen
            </button>
          </div>
        </div>
      )}

      {/* Punkt löschen (nur wenn nicht Teil eines Bogens oder nur Zwischenpunkt) */}
      {!arc && (
        <button
          onClick={() => onDelete?.(point.id)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded hover:bg-red-100"
        >
          <Trash2 size={16} />
          Punkt löschen
        </button>
      )}
    </div>
  );
}

// Wand-Eigenschaften
function WallProperties({ wall, startPoint, endPoint, componentTypes, onUpdate, onDelete, onDivideWall, onAddOpening, onUpdateOpening, onDeleteOpening }) {
  const [expandedOpening, setExpandedOpening] = useState(null);
  const [divideMode, setDivideMode] = useState('count'); // 'count' oder 'length'
  const [divideValue, setDivideValue] = useState(2);
  const length = distance(startPoint, endPoint);
  const isExterior = wall.spaceIds.length === 1;

  // Öffnungsfläche berechnen
  const openingsArea = (wall.openings || []).reduce((sum, op) => {
    return sum + (op.width * op.height) / 1000000; // mm² zu m²
  }, 0);

  return (
    <div className="p-3 space-y-3">
      {/* Info */}
      <div className="text-sm text-gray-600 space-y-1">
        <div className="flex justify-between">
          <span>Länge:</span>
          <span className="font-medium">{formatLength(length)} m</span>
        </div>
        <div className="flex justify-between">
          <span>Typ:</span>
          <span className="font-medium">{isExterior ? 'Außenwand' : 'Innenwand'}</span>
        </div>
        <div className="flex justify-between">
          <span>Öffnungsfläche:</span>
          <span className="font-medium">{openingsArea.toFixed(2)} m²</span>
        </div>
      </div>

      {/* Raumbildend Toggle */}
      <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
        <div>
          <label className="text-sm font-medium text-gray-700">Raumbildend</label>
          <div className="text-xs text-gray-500">
            {wall.roomForming !== false
              ? 'Definiert Raumgrenzen'
              : 'Nur für Mengen (z.B. Wannenverkleidung)'}
          </div>
        </div>
        <button
          onClick={() => onUpdate?.(wall.id, { roomForming: wall.roomForming === false })}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            wall.roomForming !== false ? 'bg-blue-600' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              wall.roomForming !== false ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Wandhöhen (Trapez-Wände möglich) */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-gray-500">Höhe Start (mm)</label>
          <input
            type="number"
            value={Math.round(wall.startHeight || 2750)}
            onChange={(e) => onUpdate?.(wall.id, { startHeight: parseFloat(e.target.value) || 2750 })}
            className="w-full px-2 py-1 border rounded text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">Höhe Ende (mm)</label>
          <input
            type="number"
            value={Math.round(wall.endHeight || 2750)}
            onChange={(e) => onUpdate?.(wall.id, { endHeight: parseFloat(e.target.value) || 2750 })}
            className="w-full px-2 py-1 border rounded text-sm"
          />
        </div>
      </div>
      {(wall.startHeight || 2750) !== (wall.endHeight || 2750) && (
        <div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
          Trapez-Wand (unterschiedliche Höhen)
        </div>
      )}

      {/* Wandstärke */}
      {(() => {
        const selectedType = componentTypes.find(t => t.id === wall.typeRef);
        const effectiveThickness = selectedType?.totalThickness ?? wall.thickness ?? 125;
        const isFromType = selectedType?.totalThickness != null;

        return (
          <div>
            <label className="text-xs text-gray-500">Wandstärke (cm)</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={(effectiveThickness / 10).toFixed(1)}
                onChange={(e) => {
                  const cm = parseFloat(e.target.value);
                  if (!isNaN(cm) && cm > 0) {
                    onUpdate?.(wall.id, { thickness: cm * 10 }); // cm zu mm
                  }
                }}
                disabled={isFromType}
                className={`flex-1 px-2 py-1 border rounded text-sm ${isFromType ? 'bg-gray-100 text-gray-500' : ''}`}
                step="0.5"
                min="1"
              />
              {isFromType && (
                <span className="text-xs text-blue-600" title={`Aus Bauteiltyp: ${selectedType.name}`}>
                  (vom Typ)
                </span>
              )}
            </div>
            <div className="text-xs text-gray-400 mt-0.5">
              Innenkante = gezeichnet, Stärke springt {wall.flipped ? 'rechts' : 'links'}
            </div>
          </div>
        );
      })()}

      {/* Wand wenden (Innen/Außen-Seite tauschen) */}
      <button
        onClick={() => onUpdate?.(wall.id, { flipped: !wall.flipped })}
        className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded border transition-colors ${
          wall.flipped
            ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
            : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
        }`}
        title="Wandstärke-Richtung umkehren (Innen/Außen tauschen)"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 1l4 4-4 4" />
          <path d="M3 11V9a4 4 0 0 1 4-4h14" />
          <path d="m7 23-4-4 4-4" />
          <path d="M21 13v2a4 4 0 0 1-4 4H3" />
        </svg>
        Wand wenden {wall.flipped ? '(gewendet)' : ''}
      </button>

      {/* Bauteiltyp */}
      <div>
        <label className="text-xs text-gray-500">Bauteiltyp</label>
        <select
          value={wall.typeRef || ''}
          onChange={(e) => onUpdate?.(wall.id, { typeRef: e.target.value || null })}
          className="w-full px-2 py-1 border rounded text-sm"
        >
          <option value="">- Kein Typ -</option>
          {componentTypes
            .filter(t => t.category === 'wall')
            .map(type => (
              <option key={type.id} value={type.id}>
                {type.shortCode || type.name}
              </option>
            ))}
        </select>
      </div>

      {/* Öffnungen */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs text-gray-500 font-medium">Öffnungen ({(wall.openings || []).length})</label>
          <div className="flex gap-1">
            <button
              onClick={() => onAddOpening?.(wall.id, { type: 'window' })}
              className="p-1 hover:bg-blue-100 rounded text-blue-600"
              title="Fenster hinzufügen"
            >
              <Square size={16} />
            </button>
            <button
              onClick={() => onAddOpening?.(wall.id, { type: 'door' })}
              className="p-1 hover:bg-orange-100 rounded text-orange-600"
              title="Tür hinzufügen"
            >
              <DoorOpen size={16} />
            </button>
          </div>
        </div>

        {(wall.openings || []).length === 0 ? (
          <div className="text-xs text-gray-400 text-center py-2">Keine Öffnungen</div>
        ) : (
          <div className="space-y-1">
            {wall.openings.map((opening) => (
              <OpeningItem
                key={opening.id}
                opening={opening}
                wallId={wall.id}
                wallLength={length}
                isExpanded={expandedOpening === opening.id}
                onToggle={() => setExpandedOpening(expandedOpening === opening.id ? null : opening.id)}
                onUpdate={onUpdateOpening}
                onDelete={onDeleteOpening}
              />
            ))}
          </div>
        )}
      </div>

      {/* Wand teilen (Issue #11) */}
      {onDivideWall && (
        <div className="space-y-2 p-2 bg-gray-50 rounded border">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Scissors size={14} />
            <span>Wand teilen</span>
          </div>
          <div className="flex gap-2">
            <select
              value={divideMode}
              onChange={(e) => setDivideMode(e.target.value)}
              className="px-2 py-1 border rounded text-xs"
            >
              <option value="count">Anzahl</option>
              <option value="length">Abstand</option>
            </select>
            <input
              type="number"
              value={divideValue}
              onChange={(e) => setDivideValue(parseFloat(e.target.value) || 2)}
              className="flex-1 px-2 py-1 border rounded text-xs"
              min={divideMode === 'count' ? 2 : 100}
              step={divideMode === 'count' ? 1 : 100}
            />
            <span className="text-xs text-gray-500 self-center">
              {divideMode === 'count' ? 'Teile' : 'mm'}
            </span>
          </div>
          <button
            onClick={() => {
              if (divideMode === 'count') {
                onDivideWall(wall.id, { segmentCount: Math.max(2, Math.round(divideValue)) });
              } else {
                onDivideWall(wall.id, { segmentLength: Math.max(100, divideValue) });
              }
            }}
            className="w-full px-3 py-1.5 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
          >
            In {divideMode === 'count'
              ? `${Math.max(2, Math.round(divideValue))} Segmente`
              : `Segmente à ${(divideValue / 1000).toFixed(2)}m`} teilen
          </button>
          <div className="text-xs text-gray-400">
            Rest: {divideMode === 'count'
              ? `je ${formatLength(length / Math.max(2, Math.round(divideValue)))}m`
              : `${formatLength(length % Math.max(100, divideValue))}m`}
          </div>
        </div>
      )}

      <button
        onClick={() => onDelete?.(wall.id)}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded hover:bg-red-100"
      >
        <Trash2 size={16} />
        Wand löschen
      </button>
    </div>
  );
}

// Einzelne Öffnung
function OpeningItem({ opening, wallId, wallLength, isExpanded, onToggle, onUpdate, onDelete }) {
  const handleChange = (key, value) => {
    onUpdate?.(wallId, opening.id, { [key]: value });
  };

  const positionMm = opening.position * wallLength;

  return (
    <div className="border rounded bg-gray-50">
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-2 text-left hover:bg-gray-100"
      >
        <div className="flex items-center gap-2">
          {opening.type === 'door' ? (
            <DoorOpen size={14} className="text-orange-500" />
          ) : (
            <Square size={14} className="text-blue-500" />
          )}
          <span className="text-xs font-medium">
            {opening.name || (opening.type === 'door' ? 'Tür' : 'Fenster')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">
            {opening.width}×{opening.height}
          </span>
          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </div>
      </button>

      {/* Details */}
      {isExpanded && (
        <div className="p-2 border-t space-y-2">
          {/* Name */}
          <div>
            <label className="text-xs text-gray-500">Bezeichnung</label>
            <input
              type="text"
              value={opening.name || ''}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={opening.type === 'door' ? 'Tür' : 'Fenster'}
              className="w-full px-2 py-1 border rounded text-xs"
            />
          </div>

          {/* Typ */}
          <div>
            <label className="text-xs text-gray-500">Typ</label>
            <select
              value={opening.type}
              onChange={(e) => handleChange('type', e.target.value)}
              className="w-full px-2 py-1 border rounded text-xs"
            >
              <option value="window">Fenster</option>
              <option value="door">Tür</option>
            </select>
          </div>

          {/* Maße */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-500">Breite (mm)</label>
              <input
                type="number"
                value={opening.width || 1000}
                onChange={(e) => handleChange('width', parseInt(e.target.value) || 1000)}
                className="w-full px-2 py-1 border rounded text-xs"
                step={50}
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Höhe (mm)</label>
              <input
                type="number"
                value={opening.height || 1200}
                onChange={(e) => handleChange('height', parseInt(e.target.value) || 1200)}
                className="w-full px-2 py-1 border rounded text-xs"
                step={50}
              />
            </div>
          </div>

          {/* Position */}
          <div>
            <label className="text-xs text-gray-500">
              Position: {formatLength(positionMm)} m von Start
            </label>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={opening.position || 0.5}
              onChange={(e) => handleChange('position', parseFloat(e.target.value))}
              className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
            />
          </div>

          {/* Brüstungshöhe (nur Fenster) */}
          {opening.type === 'window' && (
            <div>
              <label className="text-xs text-gray-500">Brüstungshöhe (mm)</label>
              <input
                type="number"
                value={opening.sillHeight || 900}
                onChange={(e) => handleChange('sillHeight', parseInt(e.target.value) || 900)}
                className="w-full px-2 py-1 border rounded text-xs"
                step={50}
              />
            </div>
          )}

          {/* Löschen */}
          <button
            onClick={() => onDelete?.(wallId, opening.id)}
            className="w-full flex items-center justify-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
          >
            <Trash2 size={12} />
            Öffnung löschen
          </button>
        </div>
      )}
    </div>
  );
}

// Raum-Eigenschaften
function SpaceProperties({ space, polygon, componentTypes = [], onUpdate, onDelete }) {
  const area = polygon ? polygon.reduce((sum, p, i, arr) => {
    const j = (i + 1) % arr.length;
    return sum + (arr[i].x * arr[j].y - arr[j].x * arr[i].y);
  }, 0) / 2 : 0;

  return (
    <div className="p-3 space-y-3">
      {/* Name */}
      <div>
        <label className="text-xs text-gray-500">Name</label>
        <input
          type="text"
          value={space.name || ''}
          onChange={(e) => onUpdate?.(space.id, { name: e.target.value })}
          className="w-full px-2 py-1 border rounded text-sm"
          placeholder="Raumname"
        />
      </div>

      {/* Info */}
      <div className="text-sm text-gray-600 space-y-1">
        <div className="flex justify-between">
          <span>Fläche:</span>
          <span className="font-medium">{formatArea(Math.abs(area))} m²</span>
        </div>
        <div className="flex justify-between">
          <span>Ecken:</span>
          <span className="font-medium">{space.pointIds?.length || 0}</span>
        </div>
      </div>

      {/* Hinweis: Decke/Boden auf Geschoss-Ebene */}
      <div className="p-2 bg-purple-50 rounded text-xs text-purple-700">
        Boden- und Deckenaufbau werden auf Geschoss-Ebene definiert
        (des einen Raum Boden ist des anderen Raum Decke).
      </div>

      {/* Farbe */}
      <div>
        <label className="text-xs text-gray-500">Farbe</label>
        <div className="flex gap-1 mt-1">
          {[0, 1, 2, 3, 4, 5, 6, 7].map(idx => (
            <button
              key={idx}
              onClick={() => onUpdate?.(space.id, { colorIndex: idx })}
              className={`w-6 h-6 rounded border-2 ${
                space.colorIndex === idx ? 'border-gray-800' : 'border-transparent'
              }`}
              style={{
                backgroundColor: [
                  '#bfdbfe', '#bbf7d0', '#fde68a', '#fecaca',
                  '#e9d5ff', '#cffafe', '#fed7aa', '#f5d0fe'
                ][idx]
              }}
            />
          ))}
        </div>
      </div>

      <button
        onClick={() => onDelete?.(space.id)}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded hover:bg-red-100"
      >
        <Trash2 size={16} />
        Raum löschen
      </button>
    </div>
  );
}
